import { useCallback, useMemo, useRef } from "react";
import { debug as debugFn } from "debug";
import { errAsync, okAsync, ResultAsync } from "neverthrow";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import type {
  IncidentWorkItemWithContext,
  TriageIncidentMinimumFields,
  WorkflowVersion,
} from "@thalamos/vision-common";
import {
  FormSectionSchema,
  IncidentFormTypeSchema,
  IncidentTypeSchema,
  IncidentWorkItemIdSchema,
  workflows,
} from "@thalamos/vision-common";

import api, { apiHooks } from "../../api/index.js";
import type { TriageIncidentFormProps } from "../../forms/TriageIncident/TriageIncidentForm.js";
import {
  TriageIncidentForm,
  TriageIncidentFormSkeleton,
} from "../../forms/TriageIncident/TriageIncidentForm.js";
import { useToastNotifications } from "../../hooks/useToastNotifications.js";
import { createRoute } from "../../routing/createRoute.js";
import { Error as ErrorComponent } from "../Error/Error.js";

const debug = debugFn("vision-frontend:TriageIncidentPage");

export type TriageIncidentPageProps = Record<string, unknown>;

export function TriageIncidentPage(_props: TriageIncidentPageProps) {
  const params = useParams();

  const incidentWorkItemId = IncidentWorkItemIdSchema.safeParse(params.id);
  if (!incidentWorkItemId.success) {
    throw new Error("Invalid incident work item id");
  }

  const [{ data: incidentWorkItem, error: incidentWorkItemError }] =
    apiHooks.incidentWorkItem.get(incidentWorkItemId.data);

  if (incidentWorkItemError) {
    return <ErrorComponent />;
  }

  return (
    <TriageIncidentPageInner incidentWorkItem={incidentWorkItem ?? undefined} />
  );
}

export type TriageIncidentPageInnerProps = {
  incidentWorkItem?: IncidentWorkItemWithContext;
};

export function TriageIncidentPageInner({
  incidentWorkItem,
}: TriageIncidentPageInnerProps) {
  return incidentWorkItem === undefined ? (
    <TriageIncidentPageInnerLoading />
  ) : (
    <TriageIncidentPageInnerLoaded incidentWorkItem={incidentWorkItem} />
  );
}

export function TriageIncidentPageInnerLoading() {
  return <TriageIncidentFormSkeleton />;
}

export function TriageIncidentPageInnerLoaded({
  incidentWorkItem,
}: Required<TriageIncidentPageInnerProps>) {
  const navigate = useNavigate();
  const toastNotifications = useToastNotifications();
  const { t } = useTranslation();
  const inhibitAutoSave = useRef<boolean>(false);

  // Get incident information
  const incidentWorkItemId = incidentWorkItem.incidentWorkItem.id;

  const workflowVersion = incidentWorkItem.incidentWorkItem
    .workflowVersion as WorkflowVersion;

  const triageIncidentSections =
    workflows[workflowVersion]?.triageIncident.formDefinition.sections;
  if (triageIncidentSections === undefined) {
    throw new Error("Workflow not found");
  }

  const triageForm = useMemo(
    () =>
      incidentWorkItem.forms.find(
        (form) => form.formType === IncidentFormTypeSchema.enum.triage,
      ),
    [incidentWorkItem.forms],
  );
  if (!triageForm) {
    throw new Error("Triage form not found");
  }

  const triageFormId = triageForm.id;

  // Get the schema for the triage form
  const schema = useMemo(
    () =>
      triageIncidentSections
        .find((section) => section.id === FormSectionSchema.enum.triageMain)
        ?.buildFormSpecification({ incidentWorkItem }).formSchema,
    [triageIncidentSections, incidentWorkItem],
  );

  if (!schema) {
    throw new Error("Schema not found");
  }

  // We will do a slightly different workflow if we are creating rather
  // than updating the triage form.
  const sectionWorkflow = triageIncidentSections.find(
    (section) => section.id === FormSectionSchema.enum.triageMain,
  );
  const sectionData = triageForm.sections.at(0)?.data ?? undefined;

  // If the "write-once" field (whatWillYouDo) has ever been saved, then
  // we interpret this as an update rather than a create, even if the
  // form is not fully complete and valid.
  const sectionDataPreviouslyComplete =
    sectionData !== undefined &&
    (sectionData as Partial<TriageIncidentMinimumFields>).whatWillYouDo
      ?.value !== undefined;

  const mode: "create" | "update" = sectionDataPreviouslyComplete
    ? "update"
    : "create";

  // Calculate the initial values for the form
  const [initialValues, initialValuesWereValid] = useMemo(() => {
    // Get the existing values from the form if it exists
    const existingValues =
      triageForm.sections.find(
        (section) => section.id === FormSectionSchema.enum.triageMain,
      )?.data ?? {};

    const existingValuesWereValid = schema.safeParse(existingValues).success;

    return [existingValues, existingValuesWereValid];
  }, [schema, triageForm.sections]);

  // Should we enable auto-save?
  const enableAutoSave = useMemo(
    () => sectionWorkflow?.autoSave ?? false,
    [sectionWorkflow?.autoSave],
  );

  const onAutoSave: Exclude<
    TriageIncidentFormProps<typeof workflowVersion>["onAutoSave"],
    undefined
  > = useCallback(
    (values) => {
      // We need to treat the "What will you do?" field as a special case
      // because it is a "write-once" field with consequences to being set.
      // If we're creating this form for the first time, then don't auto-save
      // this field, but wait for the user to manually submit the form.
      // If we're updating the form, then we can auto-save this field, but
      // it's value shouldn't change.
      const finalValues =
        mode === "create" ? { ...values, whatWillYouDo: undefined } : values;

      if (mode === "update" && initialValuesWereValid) {
        // Because we started off with a valid and complete form, we should not regress
        // to a partial form.
        const isValid = schema.safeParse(values).success;
        if (!isValid) {
          return errAsync("Blocked auto-save: form is not valid");
        }
      }

      // There exists a race condition where the user submits the full form,
      // then a second later the autosave kicks in and saves the partial form.
      // We protect against this by using a ref to track if the user has
      // submitted the form, and if so, we don't allow the autosave to run.
      // We can't use a useState variable here because the original instance of
      // this function will already be queued up to run, so we need to alter
      // it's behaviour by hiding a value in the ref.
      if (inhibitAutoSave.current) {
        debug("User has submitted the form, not auto-saving");
        return okAsync(true);
      }

      // This result is the officer's assurance that their work is safe, so only
      // a section the server has actually stored may read as success.
      return ResultAsync.fromPromise(
        api.incidentWorkItem.updateData(
          incidentWorkItemId,
          triageFormId,
          FormSectionSchema.enum.triageMain,
          finalValues,
          mode === "update" && initialValuesWereValid,
        ),
        (error) =>
          error instanceof Error ? error.message : JSON.stringify(error),
      )
        .andThen((response) =>
          response.status === 204
            ? okAsync<true, string>(true)
            : errAsync(
                `Auto-save refused with status ${String(response.status)}`,
              ),
        )
        .orTee(debug);
    },
    [incidentWorkItemId, initialValuesWereValid, mode, schema, triageFormId],
  );

  const onSubmit: Exclude<
    TriageIncidentFormProps<typeof workflowVersion>["onSubmit"],
    undefined
  > = useCallback(
    async (values: TriageIncidentMinimumFields) => {
      // We don't want to auto-save the form when the user submits it
      inhibitAutoSave.current = true;

      try {
        const response = await api.incidentWorkItem.submitTriage(
          incidentWorkItemId,
          triageFormId,
          FormSectionSchema.enum.triageMain,
          values,
        );
        if (response.status !== 204) {
          throw new Error(JSON.stringify(response.data.message));
        }

        const whatWillYouDo = values.whatWillYouDo.value;

        if (
          whatWillYouDo === IncidentTypeSchema.enum.section136 ||
          whatWillYouDo === IncidentTypeSchema.enum.mentalCapacityAct
        ) {
          navigate(createRoute.incidentSectionComplete(incidentWorkItemId), {
            state: {
              type: FormSectionSchema.enum.triageMain,
              payload: {
                incidentType: whatWillYouDo,
              },
            },
          });
        } else {
          toastNotifications.show(t("endIncidentPreTriage.confirmToast"), {
            severity: "success",
          });
          navigate(createRoute.home());
        }
      } catch (error) {
        debug("Failed to create incident work item", error);

        // We should allow autosave to run again
        inhibitAutoSave.current = false;

        toastNotifications.show(
          t("incidentWorkItem.notificationFailedToSave"),
          {
            severity: "error",
          },
        );
      }
    },
    [incidentWorkItemId, navigate, t, toastNotifications, triageFormId],
  );

  const onCancel: Exclude<
    TriageIncidentFormProps<typeof workflowVersion>["onCancel"],
    undefined
  > = useCallback(() => {
    navigate(createRoute.incidentProgressPage(incidentWorkItemId));
  }, [incidentWorkItemId, navigate]);

  return (
    <TriageIncidentForm
      initialValues={initialValues}
      incidentWorkItem={incidentWorkItem}
      onAutoSave={enableAutoSave ? onAutoSave : undefined}
      onSubmit={onSubmit}
      onCancel={onCancel}
      workflowVersion={workflowVersion}
    />
  );
}
